import { createSignal } from 'solid-js';

/**
 * The live REPORT pages' data edge, for the header badge ("GP50 - LIVE (last maneuver 2min ago)").
 *
 * The live report pages (LIVE PERFORMANCE, LIVE MANEUVERS) never subscribe to the telemetry stream:
 * they poll `events_aggregate` / `/maneuvers` instead, because graded maneuvers and BIN 10 aggregates
 * land in PostgreSQL from async Python jobs that the WebSocket cannot carry. So the header could say
 * nothing at all about currency on exactly the pages whose currency a coach most wants to check —
 * "is this actually up to date, or has grading stopped?" is the question those pages exist to answer.
 *
 * Each page publishes its own newest PLOTTED datum here on mount and clears it on cleanup.
 *
 * The badge carries NO staleness threshold, deliberately. The two pages would need very different
 * ones — a ten-minute gap between bin aggregates means something stopped, the same gap between
 * maneuvers usually just means a long leg — and two identical ages rendered in different colours
 * reads as a glitch, because the rule that separates them is invisible. The age itself is the signal;
 * a stalled FEED is already reported separately by the live map's stream-status badge.
 *
 * Deliberately a PLAIN signal, not a `createSyncSignal`: module state is per browsing context, so a
 * popped-out window gets its own value and shows its own page's edge. A synced signal would broadcast
 * one page's timestamp into every other window's header.
 */
export interface LiveReportEdge {
  /** Newest plotted datum, epoch ms. */
  atMs: number;
  /**
   * Identity key for `clearLiveReportEdge` — e.g. "maneuver", "aggregate". Never displayed; it only
   * has to be unique per publishing page.
   */
  what: string;
  /**
   * Badge prefix, read verbatim into "(<label> 2min ago)". Each page names its own subject — "last
   * maneuver" on LIVE MANEUVERS, "last points" on LIVE PERFORMANCE — because a generic "updated" says
   * nothing about WHAT is that old, which is the whole question on a page that plots one thing and
   * polls another.
   */
  label: string;
}

const [liveReportEdge, setLiveReportEdgeSignal] = createSignal<LiveReportEdge | null>(null);

export { liveReportEdge };

/** Publish this page's newest plotted datum. Pass null when there is nothing on the plot yet. */
export function setLiveReportEdge(edge: LiveReportEdge | null): void {
  setLiveReportEdgeSignal(edge);
}

/**
 * Drop this page's claim on the header badge.
 *
 * Guarded by identity: a page unmounting AFTER its replacement has already published would otherwise
 * clear the new page's edge. Solid runs the incoming component's effects before the outgoing one's
 * cleanup in some transitions, so an unconditional clear is not safe here.
 */
export function clearLiveReportEdge(what: string): void {
  setLiveReportEdgeSignal((prev) => (prev == null || prev.what === what ? null : prev));
}

/** "just now" / "2min ago" / "1h 05m ago" — compact enough for a header. */
export function formatLiveReportEdgeAge(ageMs: number): string {
  const totalSec = Math.max(0, Math.floor(ageMs / 1000));
  if (totalSec < 30) { return 'just now'; }
  if (totalSec < 60) { return `${totalSec}s ago`; }
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) { return `${totalMin}min ago`; }
  return `${Math.floor(totalMin / 60)}h ${String(totalMin % 60).padStart(2, '0')}m ago`;
}
